import type { ClientMessageData } from '@shared/api';
import { useEffect, useMemo, useRef } from 'react';
import type {
  FlatIndexLocationWithAlign,
  FollowOutputScalarType,
  StateSnapshot,
} from 'react-virtuoso';

interface Args {
  id: number;
  messages: ClientMessageData[];
  stateSnapshot?: StateSnapshot | null;
  firstItemIndex: number;
}

export const useListLayout = ({ id, messages, stateSnapshot, firstItemIndex }: Args) => {
  const lastMessageIndex = messages.length - 1;
  const lastMessage = messages[lastMessageIndex];

  const initialTopMostItemIndex: FlatIndexLocationWithAlign = useMemo(() => {
    if (stateSnapshot) {
      return {
        index: stateSnapshot.ranges[0].startIndex,
        offset: stateSnapshot.scrollTop,
      };
    }

    return { index: 'LAST' };
  }, [stateSnapshot]);

  // Relative to total number of prev messages
  const lastMessageIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (lastMessage) {
      lastMessageIdRef.current = lastMessage.id;
    }
  }, [lastMessage?.id, id]);

  const followOutput = (isAtBottom: boolean): FollowOutputScalarType => {
    if (lastMessageIdRef.current === lastMessage?.id) {
      return false;
    }

    if (lastMessage?.id !== undefined) {
      lastMessageIdRef.current = lastMessage.id;
    }

    return isAtBottom;
  };

  return {
    firstItemIndex,
    initialTopMostItemIndex,
    followOutput,
  };
};
